import type {
	CareersAnswerMatch,
	CareersApplicationSource,
	CareersApplicationStatus,
	CareersCvUploadStatus,
	CareersEndOutcome,
	CareersEndPair,
	CareersEndReason,
	CareersEndReasonByOutcome,
	CareersEndReasonNotGiven,
	CareersFeedbackDecision,
	CareersJobStatus,
	CareersPostingChannel,
} from "./types";

/** TanStack Query key prefixes. Writes invalidate by PREFIX, so every cached variant of a list refreshes. */
export const CAREERS_QUERY_KEY = {
	JOBS: "careersJobs",
	APPLICATIONS: "careersApplications",
	/** One application's full detail — the profile page. */
	APPLICATION: "careersApplication",
	/** One application's current public exclusion/debarment search batch. */
	BACKGROUND_CHECKS: "careersBackgroundChecks",
	/** The chosen department's provider-level Careers automation settings. */
	AUTOMATIONS: "careersAutomations",
	/** One application's reviewer-recorded decisions from official public-record websites. */
	MANUAL_BACKGROUND_CHECKS: "careersManualBackgroundChecks",
	/** The person with an email or phone, for the add dialog's duplicate check. */
	CANDIDATE_MATCH: "careersCandidateMatch",
	CV_UPLOADS: "careersCvUploads",
	/** The first few applications of one job, for the Jobs table's applicants peek. Its own shape: never under APPLICATIONS. */
	JOB_APPLICANTS_PREVIEW: "careersJobApplicantsPreview",
	/** A stored CV's bytes, held as a Blob — never as an object URL, which would outlive its component. */
	CV_FILE: "careersCvFile",
	/** A Word / text CV's extracted text. */
	CV_TEXT: "careersCvText",
	/** Who a note can @mention in the department (its recruiters and interviewers). */
	MENTIONABLE_USERS: "careersMentionableUsers",
	/** One application's SMS thread (the person's, shared with the Hiring Hub) and its unread count. */
	MESSAGES: "careersMessages",
	/** The department's chat inbox: every application with a thread, and this viewer's unread counts. */
	CONVERSATIONS: "careersConversations",
	/** Whether the caller gets Careers in a department (the flag, and a hiring role there or support). */
	ACCESS: "careersAccess",
} as const;

/**
 * The profile's Messages tab. There is no push channel, so the poll IS how soon a reply shows: as fast as the Hiring
 * Hub's open conversation while the tab is shown. Hidden, the thread is read once (for its unread badge) and again
 * on focus.
 */
export const MESSAGES_POLL_MS = 10_000;
/** Public-record checks normally finish quickly; keep the Review card moving without a manual refresh. */
export const BACKGROUND_CHECKS_POLL_MS = 5_000;
/** Retries of a failed read receipt (with TanStack's backoff, 1s then 2s) before the unread count is put back. */
export const MARK_READ_RETRIES = 2;

/** The Messages tab's thread: tall enough for a few exchanges, short enough to keep the page's facts in reach. */
export const MESSAGES_LAYOUT = {
	THREAD_HEIGHT_PX: 380,
	/** Below sm the card is narrow and the page scrolls anyway; a shorter well keeps the composer on screen. */
	THREAD_HEIGHT_COMPACT_PX: 300,
} as const;

/** The backend's page ceiling for `GET /careers/applications` — the list query walks pages of this size. */
export const APPLICATIONS_PAGE_SIZE = 500;

/**
 * The Jobs table's applicants peek: how many names it lists (one page of the list endpoint, newest first) before
 * "+N more — open Pipeline".
 */
export const JOB_APPLICANTS_PREVIEW = { LIMIT: 10 } as const;

/**
 * Every Jobs-table peek (applicants, screening questions): how long a pointer or focus rests on the cell before it
 * opens / leaves before it closes — long enough that sweeping the pointer down the table doesn't flash a popover
 * per row.
 */
export const JOB_CELL_PEEK = { ENTER_DELAY_MS: 300, LEAVE_DELAY_MS: 150 } as const;

/**
 * A new job's steps, in order. The first is where new applicants land; the last is the job's hired step, so
 * moving someone there (or pressing Mark hired) marks them hired. The dialog lets the user rename, reorder
 * or unmark any of them.
 */
export const DEFAULT_JOB_STEPS: readonly { labelKey: string; isHiredStep: boolean }[] = [
	{ labelKey: "careers.job_dialog.default_step.review", isHiredStep: false },
	{ labelKey: "careers.job_dialog.default_step.interview", isHiredStep: false },
	{ labelKey: "careers.job_dialog.default_step.offer", isHiredStep: false },
	{ labelKey: "careers.job_dialog.default_step.hired", isHiredStep: true },
];

/** A new job's evaluation criteria, in order. The dialog lets the user rename, reorder, remove or add to them. */
export const DEFAULT_JOB_CRITERIA_KEYS: readonly string[] = [
	"careers.job_dialog.default_criterion.technical",
	"careers.job_dialog.default_criterion.communication",
	"careers.job_dialog.default_criterion.experience",
];

/** Server rules mirrored so the dialog can block a save the backend would reject. */
export const JOB_LIMITS = {
	MIN_STEPS: 2,
	MAX_STEPS: 20,
	MAX_QUESTIONS: 50,
	MAX_CRITERIA: 10,
	MIN_OPENINGS: 1,
	MAX_OPENINGS: 1000,
} as const;

/** In display order. Labels only — nothing is actually posted anywhere. */
export const POSTING_CHANNELS: readonly CareersPostingChannel[] = ["careers_page", "linkedin", "indeed", "nurse_boards"];

export const POSTING_CHANNEL_LABEL_KEY: Record<CareersPostingChannel, string> = {
	careers_page: "careers.destination.careers_page",
	linkedin: "careers.destination.linkedin",
	indeed: "careers.destination.indeed",
	nurse_boards: "careers.destination.nurse_boards",
};

export const POSTING_CHANNEL_CAPTION_KEY: Record<CareersPostingChannel, string> = {
	careers_page: "careers.destination.careers_page.caption",
	linkedin: "careers.destination.linkedin.caption",
	indeed: "careers.destination.indeed.caption",
	nurse_boards: "careers.destination.nurse_boards.caption",
};

/** The backend's bound on an end note (a longer one is a 422). The End process dialog's field stops typing there. */
export const END_NOTE_MAX_LENGTH = 500;

/** Low → high, the order the feedback form offers them. */
export const FEEDBACK_DECISIONS: readonly CareersFeedbackDecision[] = ["strong_no", "no", "neutral", "yes", "strong_yes"];

export const FEEDBACK_DECISION_LABEL_KEY: Record<CareersFeedbackDecision, string> = {
	strong_no: "careers.rating.strong_no",
	no: "careers.rating.no",
	neutral: "careers.rating.neutral",
	yes: "careers.rating.yes",
	strong_yes: "careers.rating.strong_yes",
};

/** A criterion's score scale, low → high — the backend's ihh_feedback_score_range. */
export const FEEDBACK_SCORES: readonly number[] = [1, 2, 3, 4, 5];
export const MAX_FEEDBACK_SCORE = FEEDBACK_SCORES[FEEDBACK_SCORES.length - 1];

/** In process → Hired → Ended: the order every status choice and filter offers them. */
export const APPLICATION_STATUSES: readonly CareersApplicationStatus[] = ["in_process", "hired", "ended"];

export const APPLICATION_STATUS_LABEL_KEY: Record<CareersApplicationStatus, string> = {
	in_process: "careers.status.in_process",
	hired: "careers.status.hired",
	ended: "careers.status.ended",
};

/** Live, then Closed — the Jobs table's Status choices. */
export const JOB_STATUSES: readonly CareersJobStatus[] = ["live", "closed"];

export const JOB_STATUS_LABEL_KEY: Record<CareersJobStatus, string> = {
	live: "careers.jobs.status.live",
	closed: "careers.jobs.status.closed",
};

/** A screening answer's verdict, in the order the CV review's pick-list offers it. */
export const ANSWER_MATCHES: readonly CareersAnswerMatch[] = ["match", "mismatch", "unknown"];

export const ANSWER_MATCH_LABEL_KEY: Record<CareersAnswerMatch, string> = {
	match: "careers.match.match",
	mismatch: "careers.match.mismatch",
	unknown: "careers.match.unknown",
};

/** The four ways a process ends, in the order the End process dialog offers them. */
export const END_OUTCOMES: readonly CareersEndOutcome[] = ["rejected", "withdrew", "declined_offer", "no_reason"];

/** Each outcome's reasons, in display order - the pairs the backend accepts. `other` is always last; `no_reason` has none. */
export const END_REASONS: { readonly [O in CareersEndOutcome]: readonly CareersEndReasonByOutcome[O][] } = {
	rejected: ["not_enough_experience", "skills_mismatch", "failed_interview", "position_filled", "salary_too_high", "other"],
	withdrew: ["accepted_other_offer", "not_interested", "location", "personal", "no_response", "other"],
	declined_offer: ["salary", "accepted_other_offer", "role_or_title", "location_remote", "start_date", "other"],
	no_reason: [],
};

/**
 * Every outcome / reason pair, outcomes in `END_OUTCOMES` order and reasons in `END_REASONS` order. Spelled
 * out per outcome so each pair is typed as a `CareersEndPair` without a cast.
 */
export const END_CHOICES: readonly CareersEndPair[] = [
	...END_REASONS.rejected.map((reason) => ({ outcome: "rejected" as const, reason })),
	...END_REASONS.withdrew.map((reason) => ({ outcome: "withdrew" as const, reason })),
	...END_REASONS.declined_offer.map((reason) => ({ outcome: "declined_offer" as const, reason })),
];

export const END_OUTCOME_LABEL_KEY: Record<CareersEndOutcome, string> = {
	rejected: "careers.end.outcome.rejected",
	withdrew: "careers.end.outcome.withdrew",
	declined_offer: "careers.end.outcome.declined_offer",
	no_reason: "careers.end.outcome.no_reason",
};

/**
 * The outcomes that owe the candidate a rejection note: we turned them down, or ended it without saying why. One
 * who withdrew or declined our offer walked away, so there is nothing to send. The backend's
 * CAREERS_REJECTION_NOTE_OUTCOMES, which its CHECK mirrors.
 */
export const REJECTION_NOTE_OUTCOMES: ReadonlySet<CareersEndOutcome> = new Set<CareersEndOutcome>(["rejected", "no_reason"]);

/** No reason given — what an end without a reason records (see `CareersEndReasonNotGiven`). */
export const END_REASON_NOT_GIVEN: CareersEndReasonNotGiven = "not_given";

/** Keyed by reason alone: a reason two outcomes share ("Accepted another offer", "Other") reads the same in both. */
export const END_REASON_LABEL_KEY: Record<CareersEndReason, string> = {
	not_enough_experience: "careers.end.reason.not_enough_experience",
	skills_mismatch: "careers.end.reason.skills_mismatch",
	failed_interview: "careers.end.reason.failed_interview",
	position_filled: "careers.end.reason.position_filled",
	salary_too_high: "careers.end.reason.salary_too_high",
	accepted_other_offer: "careers.end.reason.accepted_other_offer",
	not_interested: "careers.end.reason.not_interested",
	location: "careers.end.reason.location",
	personal: "careers.end.reason.personal",
	no_response: "careers.end.reason.no_response",
	salary: "careers.end.reason.salary",
	role_or_title: "careers.end.reason.role_or_title",
	location_remote: "careers.end.reason.location_remote",
	start_date: "careers.end.reason.start_date",
	other: "careers.end.reason.other",
};

/**
 * Every `source` an application can carry. A posting channel keeps the name it has everywhere else on the
 * page (the job dialog, the Posted-to column), so "LinkedIn" can't read two ways.
 */
export const APPLICATION_SOURCE_LABEL_KEY: Record<CareersApplicationSource, string> = {
	...POSTING_CHANNEL_LABEL_KEY,
	referral: "careers.source.referral",
	agency: "careers.source.agency",
	other: "careers.source.other",
};

/** Every `source`, in the order the Source select offers them: the posting channels, then the off-channel ways in. */
export const APPLICATION_SOURCES: readonly CareersApplicationSource[] = [...POSTING_CHANNELS, "referral", "agency", "other"];

/** File types the backend accepts, as `FileDropZone`'s comma-separated extension list. */
export const CV_ACCEPT = ".pdf,.docx,.txt";

/** The backend's per-file ceiling (10 MB = 10,485,760 bytes). Checked before upload so a big file never leaves the browser. */
export const CV_MAX_BYTES = 10 * 1024 * 1024;

/**
 * The only content type the CV panel will put in a frame. A `blob:` URL runs in the app's own origin, so a
 * stored "PDF" whose bytes come back as anything else (text/html, say) is refused, never rendered.
 */
export const CV_PDF_CONTENT_TYPE = "application/pdf";

/**
 * How much of a long list the profile's cards show before the rest moves into a side panel. Bounded so the
 * history and notes cards stay near the height of the cards beside them, however long the candidate's trail is.
 */
export const PROFILE_PREVIEW = {
	/** "Activity log": the newest N events; "See full activity log" opens all of them. */
	EVENTS: 5,
	/** Interview notes: the newest N; "See all notes" opens all of them. */
	NOTES: 3,
	/** Feedback: the newest N; "See all feedback" opens all of them — the same as notes. */
	FEEDBACK: 3,
	/** Screening answers: the first N rows (answered, then Not asked); "See all screening answers" opens all of them. */
	ANSWERS: 4,
} as const;

/** The All notes panel while a list row's profile is read: a few note-card-shaped placeholders. */
export const NOTES_PANEL = {
	SKELETON_CARDS: 3,
	/** About one short note card (author line, chips, two lines of text). */
	SKELETON_CARD_HEIGHT_PX: 112,
} as const;

/** DOM id of the CV panel — the header's Show CV button `aria-controls` it. */
export const CV_PANEL_ID = "careers-profile-cv";

/** The Review tab's columns. */
export const REVIEW_LAYOUT = {
	/** The queue's width from `md` up: a name, a job title and a chip on one row each, without crowding the CV. */
	QUEUE_WIDTH_PX: 280,
	/** Stacked (below `lg`): most of the screen for the CV, under the candidate's details. */
	STACKED_CV_HEIGHT_VH: 70,
} as const;

/** The docked chat's width — the Hiring tab's, so the Review's See chat and the profile's Open chat open at the same size. */
export const CHAT_DOCK_WIDTH_PX = 420;

/** How long a Review decision's Undo stays on screen: long enough to notice a wrong click, short enough to move on. */
export const REVIEW_UNDO_MS = 15_000;

/**
 * The profile while the CV is open: the CV docked left (the wider column — it is what's being read), the
 * interview notes right. Heights are in `cqh` of the page's own scroll container, so the panel fills exactly
 * what the reviewer can see, whatever chrome sits above it.
 */
export const CV_LAYOUT = {
	/** Column shares, CV : notes. */
	CV_FR: 7,
	NOTES_FR: 5,
	/** Below this the notes' two dropdowns and composer stop fitting side by side. */
	NOTES_MIN_WIDTH_PX: 360,
	/** Theme spacing units the docked (md+) panel keeps from the scroller's top and bottom edges. */
	DOCK_INSET: 2,
	/** Stacked (below md): most of the screen for the CV, leaving the top of the notes in view beneath it. */
	STACKED_HEIGHT_CQH: 65,
	/** Floor for either layout, so a short window still shows a readable page of the CV. */
	MIN_HEIGHT_PX: 420,
	/** The board's CV side panel: wide enough for a page of a CV at a readable size, past the house panel's 460. */
	SIDE_PANEL_WIDTH_PX: 760,
} as const;

/**
 * At most this many files are SENT at once. An upload only stores the file (the parse runs later, in the
 * background), so this bounds the browser's open requests on a big drop, not the model's load.
 */
export const CV_UPLOAD_CONCURRENCY = 3;

/**
 * The statuses of an upload that still needs a person — what the review step lists. The backend calls these
 * "open", and caps them per job (`CV_OPEN_UPLOADS_LIMIT`).
 */
export const CV_OPEN_UPLOAD_STATUSES: readonly CareersCvUploadStatus[] = ["pending", "parsing", "parsed", "failed"];

/** The statuses the background parse will still move on its own; the review list polls while any row has one. */
export const CV_READING_STATUSES: readonly CareersCvUploadStatus[] = ["pending", "parsing"];

/** How often the review list refetches while a CV is still being read. The runner polls every 5 s; one parse takes ~7–10 s. */
export const CV_UPLOADS_POLL_MS = 4000;

/**
 * How long a CV may sit Waiting (queued, never picked up) before its card offers "Read now". Reading
 * normally starts within seconds, so this long means the queue is behind and the reviewer can hurry it.
 */
export const CV_READ_NOW_AFTER_MS = 30_000;

/** The backend's per-job cap on open uploads (409 `careers.cv.queue_full` past it). A drop is cut to what still fits. */
export const CV_OPEN_UPLOADS_LIMIT = 500;

/** From this many files, "Read CVs" asks first — the batch is big enough for its cost and wait to be worth a look. */
export const CV_LARGE_BATCH_CONFIRM_AT = 50;

/**
 * What one CV costs to read, in US dollars, for the large-batch estimate. Measured parses average ≈ $0.023 and
 * peak ≈ $0.029 at the model's list price; rounded up to match the standalone ATS's figure. A re-read is a
 * full second parse. Basis: `.context/careers-redesign-contract.md` → "Per-CV cost estimate".
 */
export const CV_ESTIMATED_COST_USD_PER_CV = 0.03;

/** Roughly how many CVs the background parse reads per minute (3 in parallel, ~7–10 s each) — for the batch's wait estimate. */
export const CV_ESTIMATED_PARSES_PER_MINUTE = 20;

/** Geometry of an upload review card while its CV is open beside the form. */
export const CV_REVIEW_PREVIEW = {
	/** Beside the form (md up): a readable page of a CV without dwarfing the card. It stays pinned while the form scrolls. */
	BESIDE_HEIGHT_PX: 560,
	/** Stacked above the form (below md): most of a phone's screen, leaving the first fields in view under it. */
	STACKED_HEIGHT_PX: 420,
	/** The Source select is one short choice; a full-width field would read as the card's main input. */
	SOURCE_MAX_WIDTH_PX: 320,
} as const;
